import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import type { CandidateFileData, MediaAttribute } from '@jobilla/nexus-sdk'
import { Link } from '@tanstack/react-router'
import {
	CheckIcon,
	ChevronDownIcon,
	CirclePlusIcon,
	ClockIcon,
	DownloadIcon,
	EyeIcon,
	FileIcon,
	FileTextIcon,
	LoaderCircleIcon,
	LockIcon,
	MailIcon,
	Trash2Icon,
	UploadIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { canDownloadScannedFile, getScanPresentation, isScanBlocked, isScanPending } from '../../shared/scan-status'

export type FileTagChangeHandler = (file: CandidateFileData, attribute: MediaAttribute | null) => void

export interface CandidateFilesProps {
	files?: CandidateFileData[]
	getEmailUrl?: (emailId: string) => string
	onUpload?: () => void
	onTagChange?: FileTagChangeHandler
	savingTagFileId?: string | null
	tagFailure?: FileTagFailure | null
	onDelete?: (file: CandidateFileData) => void
	deletingFileId?: string | null
	deleteFailedFileId?: string | null
}

export type FileTagFailure = {
	fileId: string
	requiresCleanScan: boolean
}

function formatFileSize(bytes: number): string {
	if (bytes === 0) { return '0 B' }
	const units = ['B', 'KB', 'MB', 'GB']
	const i = Math.floor(Math.log(bytes) / Math.log(1024))
	const size = bytes / Math.pow(1024, i)
	return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

function getFileIcon(mimeType: string | null) {
	if (mimeType && (mimeType === 'application/pdf' || mimeType.includes('document') || mimeType.includes('word'))) {
		return FileTextIcon
	}
	return FileIcon
}

type FileTagMenuProps = {
	file: CandidateFileData
	isSaving: boolean
	onTagChange: FileTagChangeHandler
}

function FileTagMenu({ file, isSaving, onTagChange }: FileTagMenuProps) {
	const { t } = useTranslation('common')
	const isCv = file.attribute === 'cv'
	const tagOptions: { attribute: MediaAttribute | null; label: string }[] = [
		{ attribute: null, label: t('candidateFiles.noTag') },
		{ attribute: 'cv', label: t('candidateFiles.cv') },
	]

	return (
		<Menu>
			<MenuButton
				disabled={isSaving}
				title={isCv ? t('candidateFiles.changeTag') : undefined}
				className={`inline-flex h-6 flex-shrink-0 items-center gap-1 rounded-full border text-xs font-medium cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
					isCv
						? 'border-indigo-200 bg-indigo-50 pl-2 pr-1.5 text-indigo-800 hover:bg-indigo-100'
						: 'border-dashed border-gray-300 bg-white pl-1.5 pr-2 text-gray-600 hover:border-gray-400 hover:text-gray-900'
				}`}
			>
				{isSaving
					? <LoaderCircleIcon className="size-3 animate-spin" />
					: isCv
					? null
					: <CirclePlusIcon className="size-3" />}
				{isCv ? t('candidateFiles.cv') : t('candidateFiles.tag')}
				{isCv ? <ChevronDownIcon className="size-3" /> : null}
			</MenuButton>
			<MenuItems
				anchor="bottom end"
				className="z-50 mt-1 w-44 rounded-lg border border-gray-200 bg-white p-1 shadow-lg focus:outline-none"
			>
				<p className="px-2 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
					{t('candidateFiles.tagAs')}
				</p>
				{tagOptions.map((tagOption) => {
					const isSelected = (file.attribute ?? null) === tagOption.attribute
					return (
						<MenuItem key={tagOption.attribute ?? 'none'}>
							<button
								type="button"
								onClick={() => {
									if (!isSelected) {
										onTagChange(file, tagOption.attribute)
									}
								}}
								className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-900 cursor-pointer data-[focus]:bg-gray-100"
							>
								<span
									className={tagOption.attribute
										? 'size-2.5 rounded-full bg-indigo-500'
										: 'size-2.5 rounded-full border-[1.5px] border-dashed border-gray-400'}
								/>
								<span className="flex-1 text-left">{tagOption.label}</span>
								{isSelected ? <CheckIcon className="size-3.5 text-indigo-600" /> : null}
							</button>
						</MenuItem>
					)
				})}
			</MenuItems>
		</Menu>
	)
}

export type FileItemProps = {
	file: CandidateFileData
	getEmailUrl?: (emailId: string) => string
	onTagChange?: FileTagChangeHandler
	isSavingTag?: boolean
	tagFailure?: FileTagFailure | null
	onDelete?: (file: CandidateFileData) => void
	isDeleting?: boolean
	deleteFailed?: boolean
}

export function FileItem({
	file,
	getEmailUrl,
	onTagChange,
	isSavingTag = false,
	tagFailure = null,
	onDelete,
	isDeleting = false,
	deleteFailed = false,
}: FileItemProps) {
	const { t } = useTranslation('common')
	const Icon = getFileIcon(file.mimeType)
	const sourceEmailId = file.metadata?.sourceEmailId
	const sourceEmailSubject = file.metadata?.sourceEmailSubject
	const emailUrl = sourceEmailId && getEmailUrl ? getEmailUrl(sourceEmailId) : null

	const scanKnown = file.scanStatus !== undefined
	const scanStatus = file.scanStatus ?? null
	const scan = getScanPresentation(scanStatus)
	const ScanIcon = scan.Icon
	const blocked = scanKnown && isScanBlocked(scanStatus)
	const pending = scanKnown && isScanPending(scanStatus)
	const canChangeTag = file.attribute === 'cv' || (!blocked && !pending)

	return (
		<div
			aria-busy={isDeleting}
			className={`group flex items-center gap-3 px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 ${
				isDeleting ? 'pointer-events-none opacity-60' : ''
			}`}
		>
			<div className="relative flex-shrink-0">
				<Icon className="size-5 text-gray-400" />
				{scanKnown && (
					<span
						role="img"
						aria-label={t(scan.labelKey)}
						className={`absolute -bottom-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full ring-2 ring-white group-hover:ring-gray-50 ${scan.badgeClass}`}
					>
						<ScanIcon className={`size-2.5 ${scan.spin ? 'animate-spin' : ''}`} />
					</span>
				)}
			</div>

			<div className="flex-1 min-w-0">
				<p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
				<div className="flex items-center gap-1.5 text-xs text-gray-500">
					<span>{formatFileSize(file.size)}</span>
					{scanKnown && scan.state !== 'clean' && (
						<>
							<span>·</span>
							<span className={`font-medium ${scan.tagClass}`}>{t(scan.labelKey)}</span>
						</>
					)}
					{sourceEmailId && sourceEmailSubject && (
						<>
							<span>·</span>
							{emailUrl
								? (
									<Link
										to={emailUrl}
										className="flex items-center gap-1 hover:text-gray-700 hover:underline truncate max-w-[200px]"
									>
										<MailIcon className="size-3 flex-shrink-0" />
										<span className="truncate">{sourceEmailSubject}</span>
									</Link>
								)
								: (
									<span className="flex items-center gap-1 truncate max-w-[200px]">
										<MailIcon className="size-3 flex-shrink-0" />
										<span className="truncate">{sourceEmailSubject}</span>
									</span>
								)}
						</>
					)}
				</div>
				{tagFailure
					? (
						<p role="alert" className="text-xs text-red-600">
							{tagFailure.requiresCleanScan
								? t('candidateFiles.tagAfterScan')
								: t('candidateFiles.tagFailed')}
						</p>
					)
					: null}
				{deleteFailed
					? <p role="alert" className="text-xs text-red-600">{t('candidateFiles.deleteFailed')}</p>
					: null}
			</div>

			{onTagChange
				? canChangeTag
					? <FileTagMenu file={file} isSaving={isSavingTag} onTagChange={onTagChange} />
					: pending
					? (
						<button
							type="button"
							disabled
							title={t('candidateFiles.tagAfterScan')}
							className="inline-flex h-6 flex-shrink-0 items-center gap-1 rounded-full border border-dashed border-gray-200 bg-white pl-1.5 pr-2 text-xs font-medium text-gray-400 cursor-not-allowed"
						>
							<CirclePlusIcon className="size-3" />
							{t('candidateFiles.tag')}
						</button>
					)
					: null
				: null}

			<FileActions file={file} />

			{onDelete
				? (
					<button
						type="button"
						onClick={() => onDelete(file)}
						disabled={isDeleting}
						title={t('candidateFiles.delete')}
						aria-label={t('candidateFiles.delete')}
						className="flex size-8 flex-shrink-0 items-center justify-center rounded-md text-gray-500 cursor-pointer transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed"
					>
						{isDeleting
							? <LoaderCircleIcon className="size-4 animate-spin" />
							: <Trash2Icon className="size-4" />}
					</button>
				)
				: null}
		</div>
	)
}

function FileActions({ file }: { file: CandidateFileData }) {
	const { t } = useTranslation('common')
	const scanKnown = file.scanStatus !== undefined
	const scanStatus = file.scanStatus ?? null
	const scan = getScanPresentation(scanStatus)
	const blocked = scanKnown && isScanBlocked(scanStatus)
	const pending = scanKnown && isScanPending(scanStatus)
	const accessible = !scanKnown || canDownloadScannedFile(scanStatus)

	const blockedLabel = file.scanSignature
		? `${t('fileScan.blocked')} — ${t('fileScan.signature', { signature: file.scanSignature })}`
		: t('fileScan.blocked')

	const actionClassName =
		`flex size-8 flex-shrink-0 items-center justify-center rounded-md cursor-pointer transition-colors ${
			scan.state === 'error'
				? 'text-amber-600 hover:bg-amber-50 hover:text-amber-700'
				: 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
		}`

	return blocked
		? (
			<span
				role="img"
				aria-label={blockedLabel}
				title={blockedLabel}
				className="flex size-8 flex-shrink-0 items-center justify-center rounded-md text-red-600"
			>
				<LockIcon className="size-4" />
			</span>
		)
		: pending
		? (
			<button
				type="button"
				disabled
				title={t('fileScan.pendingHint')}
				aria-label={t('fileScan.pendingHint')}
				className="flex size-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 cursor-not-allowed"
			>
				{scan.state === 'scanning'
					? <LoaderCircleIcon className="size-4 animate-spin" />
					: <ClockIcon className="size-4" />}
			</button>
		)
		: accessible
		? (
			<div className="flex flex-shrink-0 items-center">
				{file.previewUrl
					? (
						<a
							href={file.previewUrl}
							target="_blank"
							rel="noopener noreferrer"
							title={scan.state === 'error' ? t('fileScan.errorHint') : t('candidateFiles.preview')}
							aria-label={t('candidateFiles.preview')}
							className={actionClassName}
						>
							<EyeIcon className="size-4" />
						</a>
					)
					: null}
				{file.url
					? (
						<a
							href={file.url}
							download={file.fileName}
							title={scan.state === 'error' ? t('fileScan.errorHint') : t('candidateFiles.download')}
							aria-label={t('candidateFiles.download')}
							className={actionClassName}
						>
							<DownloadIcon className="size-4" />
						</a>
					)
					: null}
			</div>
		)
		: null
}

export function CvFileItem({ file }: { file: CandidateFileData }) {
	const { t } = useTranslation('common')
	const Icon = getFileIcon(file.mimeType)
	const scan = getScanPresentation(file.scanStatus ?? null)
	const showScanLabel = file.scanStatus !== undefined && scan.state !== 'clean'

	return (
		<div className="flex items-center gap-3 rounded-lg border border-gray-200 py-2 pl-3 pr-1">
			<Icon className="size-5 flex-shrink-0 text-indigo-600" />
			<div className="flex-1 min-w-0">
				<p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
				<p className="flex items-center gap-1.5 text-xs text-gray-500">
					<span>{formatFileSize(file.size)}</span>
					{showScanLabel
						? (
							<>
								<span>·</span>
								<span className={`font-medium ${scan.tagClass}`}>{t(scan.labelKey)}</span>
							</>
						)
						: null}
				</p>
			</div>
			<FileActions file={file} />
		</div>
	)
}

export function CandidateFiles({
	files = [],
	getEmailUrl,
	onUpload,
	onTagChange,
	savingTagFileId = null,
	tagFailure = null,
	onDelete,
	deletingFileId = null,
	deleteFailedFileId = null,
}: CandidateFilesProps) {
	const { t } = useTranslation('common')
	return (
		<div className="flex flex-col h-full bg-white">
			<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
				<div className="flex items-center gap-2">
					<FileTextIcon className="size-5 text-gray-500" />
					<h2 className="font-medium text-gray-900">{t('candidateFiles.title')}</h2>
					{files.length > 0 && <span className="text-sm text-gray-500">({files.length})</span>}
				</div>
				{onUpload && (
					<button
						type="button"
						onClick={onUpload}
						className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
					>
						<UploadIcon className="size-4" />
						{t('candidateFiles.upload')}
					</button>
				)}
			</div>

			<div className="flex-1 overflow-auto">
				{files.length === 0
					? (
						<div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
							<FileTextIcon className="size-12 mb-3 opacity-50" />
							<p className="text-sm">{t('candidateFiles.empty')}</p>
						</div>
					)
					: (
						<div>
							{files.map((file) => (
								<FileItem
									key={file.id}
									file={file}
									getEmailUrl={getEmailUrl}
									onTagChange={onTagChange}
									isSavingTag={savingTagFileId === file.id}
									tagFailure={tagFailure?.fileId === file.id ? tagFailure : null}
									onDelete={onDelete}
									isDeleting={deletingFileId === file.id}
									deleteFailed={deleteFailedFileId === file.id}
								/>
							))}
						</div>
					)}
			</div>
		</div>
	)
}
